import './TabiatBasvuruForm.css'
import { Button, Label, Input, Spinner, Modal, ModalBody, ModalFooter } from 'reactstrap'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import WarningModal from './WarningModal'
import Header from '../../Header'
import Loading from '../../Loading'
import ConditionsCheckBox from './ConditionsCheckBox'
import axios from 'axios'

const calculateAge = (birthDate) => {
  if (!birthDate) return null
  const dob = new Date(birthDate)
  if (isNaN(dob.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const m = today.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--
  return age
}

const resolveYasGrubu = (age) => {
  if (age == null) return null
  if (age >= 6 && age <= 7) return '6-7'
  if (age >= 8 && age <= 9) return '8-9'
  return null
}

const Row = ({ label, value, bold }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
    <span style={{ color: '#7a8a82' }}>{label}</span>
    <span style={{ fontWeight: bold ? 700 : 500, textAlign: 'right' }}>{value || '-'}</span>
  </div>
)

const TabiatBasvuruForm = () => {
  const { state } = useLocation()
  const navigate = useNavigate()

  const [applicantType, setApplicantType] = useState('self')

  const [childName, setChildName] = useState('')
  const [childSurname, setChildSurname] = useState('')
  const [childTcNo, setChildTcNo] = useState('')
  const [childBirthDate, setChildBirthDate] = useState('')

  const [veliAdi, setVeliAdi] = useState('')
  const [veliSoyadi, setVeliSoyadi] = useState('')
  const [veliTelefonNo, setVeliTelefonNo] = useState('')

  const [ilce, setIlce] = useState('')
  const [mahalle, setMahalle] = useState('')
  const [okul, setOkul] = useState('')
  const [adres, setAdres] = useState('')
  const [servisIstiyor, setServisIstiyor] = useState(null)

  const [ilceList, setIlceList] = useState([])
  const [mahalleList, setMahalleList] = useState([])
  const [okulList, setOkulList] = useState([])
  const [allMahalleler, setAllMahalleler] = useState([])
  const [allOkullar, setAllOkullar] = useState([])

  const [checkbox, setCheckbox] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isModal, setIsModal] = useState({ title: '', message: '', type: '', active: false })
  const [confirmModal, setConfirmModal] = useState({ open: false, data: null })

  const [preview, setPreview] = useState(null)

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/api/dijital_basvuru/tabiat-preview`, {
        params: { id: state?.basvuruId },
        headers: {
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('token')),
        },
      })
      .then((res) => {
        const data = res?.data || null
        setPreview(data)
        const u = data?.uye
        if (u) {
          setVeliAdi((prev) => prev || u.adi || '')
          setVeliSoyadi((prev) => prev || u.soyadi || '')
          setVeliTelefonNo((prev) => prev || (u.telefon_no || '').toString().replace(/\D/g, '').slice(0, 10))
        }
        if (data?.ilceler) setIlceList(data.ilceler)
        if (data?.mahalleler) setAllMahalleler(data.mahalleler)
        if (data?.okullar) setAllOkullar(data.okullar)
      })
      .catch(() => setPreview(null))
  }, [state?.basvuruId])

  useEffect(() => {
    setMahalle('')
    setOkul('')
    if (!ilce) {
      setMahalleList([])
      setOkulList([])
      return
    }
    setMahalleList(allMahalleler.filter((m) => m.ilce_id === ilce))
    setOkulList(allOkullar.filter((o) => o.ilce_id === ilce))
  }, [ilce, allMahalleler, allOkullar])

  const selfAge = useMemo(() => calculateAge(preview?.uye?.dogum_tarihi), [preview])
  const childAge = useMemo(() => calculateAge(childBirthDate), [childBirthDate])
  const derivedYasGrubu = useMemo(() => {
    if (applicantType === 'self') return resolveYasGrubu(selfAge)
    return resolveYasGrubu(childAge)
  }, [applicantType, selfAge, childAge])

  const toggleWarningModal = (e) => {
    if (e?.stopPropagation) e.stopPropagation()
    setIsModal((prev) => ({ ...prev, active: !prev.active }))
  }

  const showError = (type, title, message) => {
    setIsModal({ type, title: title || '', message: message || '', active: true })
  }

  const onFinish = () => {
    if (preview?.onceki_basvuru) {
      showError('server_error', 'Mükerrer Başvuru', `Bu hesapla ${preview.onceki_basvuru.hafta_no}. hafta başvurusu yapılmış.`)
      return
    }

    if (!preview?.hafta?.hafta_no) {
      showError('server_error', 'Başvuru Dönemi', preview?.hafta?.mesaj || 'Şu anda başvuru dönemi aktif değil.')
      return
    }

    if (applicantType === 'child') {
      if (childName.trim().length < 2 || childSurname.trim().length < 2 || !childBirthDate) {
        showError('failed_required')
        return
      }
      if (childTcNo.length !== 11) {
        showError('failed_tc')
        return
      }
    }

    if (!derivedYasGrubu) {
      const yas = applicantType === 'self' ? selfAge : childAge
      showError('server_error', 'Yaş Uygun Değil', `Bu etkinlik 6-9 yaş aralığındaki çocuklar içindir. (Yaş: ${yas ?? '-'})`)
      return
    }

    if (veliAdi.trim().length < 2 || veliSoyadi.trim().length < 2) {
      showError('failed_required', 'Ad Soyad Eksik', 'Ad ve soyad zorunludur.')
      return
    }
    if (veliTelefonNo.replace(/\D/g, '').length < 10) {
      showError('failed_phone', 'Telefon Hatalı', 'Telefon numarası 10 haneli olmalıdır (başında 0 olmadan).')
      return
    }

    if (!ilce) {
      showError('failed_required', 'İlçe Seçilmedi', 'Lütfen ilçe seçiniz.')
      return
    }
    if (!mahalle) {
      showError('failed_required', 'Mahalle Seçilmedi', 'Lütfen mahalle seçiniz.')
      return
    }
    if (!okul) {
      showError('failed_required', 'Okul Seçilmedi', 'Lütfen okul seçiniz.')
      return
    }
    if (adres.trim().length < 5) {
      showError('failed_required', 'Adres Eksik', 'Lütfen açık adresinizi giriniz.')
      return
    }

    if (servisIstiyor === null) {
      showError('failed_required', 'Servis Tercihi Seçilmedi', 'Lütfen servis kullanıp kullanmayacağınızı seçiniz.')
      return
    }

    if (!checkbox) {
      showError('failed_agreement')
      return
    }

    if (preview?.kontenjan?.doldu) {
      showError('server_error', 'Kontenjan Doldu', `${preview.hafta.hafta_no}. hafta için kontenjan dolmuştur.`)
      return
    }

    const yasGrubuLabel = derivedYasGrubu === '6-7' ? '6-7 Yaş' : '8-9 Yaş'
    const dersGunleri = derivedYasGrubu === '6-7' ? 'Salı ve Perşembe' : 'Çarşamba ve Cuma'
    const ilceLabel = ilceList.find((i) => i.id === ilce)?.ad || ilce
    const mahalleLabel = allMahalleler.find((m) => m.id === mahalle)?.ad || mahalle
    const okulLabel = allOkullar.find((o) => o.id === okul)?.ad || okul

    setConfirmModal({
      open: true,
      data: {
        haftaNo: preview.hafta.hafta_no,
        applicantTypeLabel: applicantType === 'self' ? 'Kendi adıma' : 'Çocuğum adına',
        katilimciAdSoyad: applicantType === 'child' ? `${childName.trim()} ${childSurname.trim()}` : `${preview?.uye?.adi ?? ''} ${preview?.uye?.soyadi ?? ''}`.trim(),
        yasGrubuLabel,
        dersGunleri,
        veliAdSoyad: `${veliAdi.trim()} ${veliSoyadi.trim()}`,
        veliTelefon: veliTelefonNo,
        ilceLabel,
        mahalleLabel,
        okulLabel,
        adres: adres.trim(),
        servisLabel: servisIstiyor ? 'Evet, kullanacağım' : 'Hayır, kullanmayacağım',
      },
    })
  }

  const submitToBackend = async () => {
    setLoading(true)

    const payload = {
      id: state?.basvuruId,
      applicant_type: applicantType,
      servis_istiyor: servisIstiyor,
      contract_approved: checkbox,
      veli: {
        adi: veliAdi.trim(),
        soyadi: veliSoyadi.trim(),
        telefon_no: veliTelefonNo.replace(/\D/g, ''),
      },
      konum: {
        ilce,
        mahalle,
        okul,
        adres: adres.trim(),
      },
    }

    if (applicantType === 'child') {
      payload.child = {
        name: childName.trim(),
        surname: childSurname.trim(),
        tc_no: childTcNo,
        birth_date: childBirthDate,
      }
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/dijital_basvuru/tabiat-create`, payload, {
        headers: {
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('token')),
        },
      })

      if (res?.data?.code === 200) {
        navigate('/success-applications', { state: { id: res?.data?.result } })
      } else {
        showError('server_error', res?.data?.title || null, res?.data?.message || 'Bir hata oluştu.')
      }
    } catch (error) {
      showError('server_error', error.response?.data?.title || 'Sunucu Hatası', error.response?.data?.message || 'Bir hata oluştu')
    } finally {
      setLoading(false)
      setConfirmModal({ open: false, data: null })
    }
  }

  const inputStyle = { width: '90vw', borderRadius: '20px' }
  const labelStyle = { paddingLeft: '1.4rem', color: '#285843', fontWeight: 'bold', fontSize: '0.9rem' }
  const sectionTitleStyle = {
    paddingLeft: '1.4rem',
    color: '#285843',
    fontWeight: 'bold',
    fontSize: '1rem',
    marginTop: '1rem',
    marginBottom: '0.25rem',
  }

  return (
    <>
      {loading ? (
        <Loading formText={'Başvurunuz alınıyor,'} text={'Lütfen bekleyiniz...'} formLodading={true} />
      ) : (
        <div className='hide-scrollbar'>
          <div style={{ backgroundColor: '#fff', position: 'sticky', top: 0 }}>
            <Header onClick={() => navigate(-1)} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={'https://app.yanibasimdakaratay.com/uploads/dijital_basvurular/' + state?.basvuru_fotografi} alt='' width={'90%'} style={{ borderRadius: '10px' }} />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              marginTop: '1rem',
              marginBottom: '0.5rem',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              paddingBottom: '0.5rem',
              fontSize: '0.9rem',
              fontStyle: 'italic',
              color: '#285843',
            }}
          >
            {state?.basvuru_aciklamasi}
          </div>

          {/* Yaş Grupları ve Ders Günleri */}
          <div
            style={{
              marginLeft: '1rem',
              marginRight: '1rem',
              marginBottom: '0.5rem',
              padding: '0.85rem 1rem',
              borderRadius: '12px',
              backgroundColor: '#eef5f1',
              border: '1px solid #285843',
              color: '#285843',
              fontSize: '0.82rem',
              lineHeight: 1.55,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.9rem' }}>📅 Yaş Grupları ve Ders Günleri</div>
            <div style={{ marginBottom: '0.3rem' }}>
              <strong>1. Grup — 6-7 Yaş:</strong> Salı ve Perşembe
            </div>
            <div>
              <strong>2. Grup — 8-9 Yaş:</strong> Çarşamba ve Cuma
            </div>
            <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>🕙 Ders Saati: 10.00 - 15.00</div>
            <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', fontStyle: 'italic', opacity: 0.85 }}>Grup, katılımcının yaşına göre otomatik atanır.</div>
          </div>

          {/* Kontenjan doluysa uyarı (sadece dolduğunda gösterilir) */}
          {preview?.kontenjan?.doldu && (
            <div
              style={{
                marginTop: '0.25rem',
                marginLeft: '1rem',
                marginRight: '1rem',
                marginBottom: '0.5rem',
                padding: '0.65rem 0.9rem',
                borderRadius: '12px',
                backgroundColor: '#fdecea',
                border: '1px solid #d9534f',
                color: '#7a1f1a',
                fontSize: '0.8rem',
                lineHeight: 1.4,
              }}
            >
              <strong>Bu haftanın kontenjanı dolmuştur.</strong>
            </div>
          )}

          {/* Başvuru dönemi kapalıysa uyarı */}
          {!preview?.hafta?.hafta_no && preview?.hafta?.mesaj && (
            <div
              style={{
                marginTop: '0.25rem',
                marginLeft: '1rem',
                marginRight: '1rem',
                marginBottom: '0.5rem',
                padding: '0.65rem 0.9rem',
                borderRadius: '12px',
                backgroundColor: '#fdecea',
                border: '1px solid #d9534f',
                color: '#7a1f1a',
                fontSize: '0.8rem',
              }}
            >
              <strong>Uyarı:</strong> {preview.hafta.mesaj}
            </div>
          )}

          {/* Daha önce başvuru varsa uyarı */}
          {preview?.onceki_basvuru && (
            <div
              style={{
                marginLeft: '1rem',
                marginRight: '1rem',
                marginBottom: '0.5rem',
                padding: '0.65rem 0.9rem',
                borderRadius: '12px',
                backgroundColor: '#fdecea',
                border: '1px solid #d9534f',
                color: '#7a1f1a',
                fontSize: '0.8rem',
              }}
            >
              <strong>Mükerrer başvuru:</strong> Bu hesapla {preview.onceki_basvuru.hafta_no}. hafta başvurusu yapılmıştır. Yeniden başvuru yapılamaz.
            </div>
          )}

          {/* Başvuru Türü */}
          <Label style={labelStyle}>Başvuru Türü</Label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem',
              marginLeft: '1rem',
              marginRight: '1rem',
              marginBottom: '0.5rem',
            }}
          >
            {[
              { value: 'self', label: 'Kendi adıma başvuruyorum' },
              { value: 'child', label: 'Çocuğum adına başvuruyorum' },
            ].map((opt) => {
              const selected = applicantType === opt.value
              return (
                <label
                  key={opt.value}
                  htmlFor={`applicant-type-${opt.value}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    padding: '0.6rem 0.9rem',
                    borderRadius: '14px',
                    border: '1px solid ' + (selected ? '#285843' : '#d8d8d0'),
                    backgroundColor: selected ? '#eef5f1' : '#fff',
                    color: '#285843',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  }}
                >
                  <input id={`applicant-type-${opt.value}`} type='radio' name='applicantType' value={opt.value} checked={selected} onChange={(e) => setApplicantType(e.target.value)} style={{ accentColor: '#285843', width: '1.05rem', height: '1.05rem' }} />
                  <span style={{ fontSize: '0.95rem', fontWeight: selected ? 600 : 400 }}>{opt.label}</span>
                </label>
              )
            })}
          </div>

          {/* Otomatik atanan yaş grubu bilgisi */}
          {derivedYasGrubu && (
            <div
              style={{
                marginLeft: '1rem',
                marginRight: '1rem',
                marginBottom: '0.5rem',
                padding: '0.6rem 0.9rem',
                borderRadius: '12px',
                backgroundColor: '#eef5f1',
                border: '1px solid #285843',
                color: '#285843',
                fontSize: '0.8rem',
              }}
            >
              Otomatik atanan grup: <strong>{derivedYasGrubu} Yaş</strong>
            </div>
          )}

          {/* Çocuk Bilgileri */}
          {applicantType === 'child' && (
            <>
              <div style={sectionTitleStyle}>Çocuk Bilgileri</div>

              <Label style={labelStyle}>Ad</Label>
              <Input style={inputStyle} className='inputStyle' value={childName} onChange={(e) => setChildName(e.target.value)} />

              <Label style={labelStyle}>Soyad</Label>
              <Input style={inputStyle} className='inputStyle' value={childSurname} onChange={(e) => setChildSurname(e.target.value)} />

              <Label style={labelStyle}>T.C. Kimlik No</Label>
              <Input style={inputStyle} className='inputStyle' value={childTcNo} maxLength={11} inputMode='numeric' onChange={(e) => setChildTcNo(e.target.value.replace(/\D/g, ''))} />

              <Label style={labelStyle}>Doğum Tarihi</Label>
              <Input style={inputStyle} className='inputStyle' type='date' value={childBirthDate} onChange={(e) => setChildBirthDate(e.target.value)} />
            </>
          )}

          {/* Veli / İletişim */}
          <div style={sectionTitleStyle}>{applicantType === 'child' ? 'Veli Bilgileri' : 'İletişim Bilgileri'}</div>

          <Label style={labelStyle}>Ad</Label>
          <Input style={inputStyle} className='inputStyle' value={veliAdi} onChange={(e) => setVeliAdi(e.target.value)} />

          <Label style={labelStyle}>Soyad</Label>
          <Input style={inputStyle} className='inputStyle' value={veliSoyadi} onChange={(e) => setVeliSoyadi(e.target.value)} />

          <Label style={labelStyle}>Telefon No</Label>
          <Input
            style={inputStyle}
            className='inputStyle'
            value={veliTelefonNo}
            maxLength={10}
            inputMode='numeric'
            placeholder='5XXXXXXXXX'
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\D/g, '').replace(/^0+/, '')
              setVeliTelefonNo(cleaned.slice(0, 10))
            }}
          />

          {/* Okul */}
          <div style={sectionTitleStyle}>Okul Bilgileri</div>

          <Label style={labelStyle}>İlçe</Label>
          <Input style={inputStyle} className='inputStyle' type='select' value={ilce} onChange={(e) => setIlce(e.target.value)}>
            <option value=''>Seçiniz</option>
            {ilceList.map((i) => (
              <option key={i.id} value={i.id}>
                {i.ad}
              </option>
            ))}
          </Input>

          <Label style={labelStyle}>Mahalle</Label>
          <Input style={inputStyle} className='inputStyle' type='select' value={mahalle} onChange={(e) => setMahalle(e.target.value)} disabled={!ilce}>
            <option value=''>{ilce ? 'Seçiniz' : 'Önce ilçe seçiniz'}</option>
            {mahalleList.map((m) => (
              <option key={m.id} value={m.id}>
                {m.ad}
              </option>
            ))}
          </Input>

          <Label style={labelStyle}>Okul</Label>
          <Input
            style={inputStyle}
            className='inputStyle'
            type='select'
            value={okul}
            onChange={(e) => setOkul(e.target.value)}
            disabled={!ilce}
          >
            <option value=''>{ilce ? 'Seçiniz' : 'Önce ilçe seçiniz'}</option>
            {okulList.map((o) => (
              <option key={o.id} value={o.id}>
                {o.ad}
              </option>
            ))}
          </Input>

          <Label style={labelStyle}>Açık Adres</Label>
          <Input style={{ ...inputStyle, minHeight: '80px' }} className='inputStyle' type='textarea' rows={3} placeholder='Ev Adresinizi Yazınız' value={adres} onChange={(e) => setAdres(e.target.value.toLocaleUpperCase('tr-TR'))} />

          {/* Servis Tercihi */}
          <div style={sectionTitleStyle}>Servis Tercihi</div>

          <div
            style={{
              marginLeft: '1rem',
              marginRight: '1rem',
              marginBottom: '0.5rem',
              padding: '0.75rem 0.9rem',
              borderRadius: '12px',
              backgroundColor: '#fff8e1',
              border: '1px solid #f0c674',
              color: '#7a5d00',
              fontSize: '0.8rem',
              lineHeight: 1.5,
            }}
          >
            🚌 <strong>Servis Bilgisi:</strong> Servis <strong>Ali Ulvi Kurucu Gençlik Merkezi</strong>'nden <strong>saat 09.30</strong>'da kalkacaktır.
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem',
              marginLeft: '1rem',
              marginRight: '1rem',
              marginBottom: '0.5rem',
            }}
          >
            {[
              { value: true, label: 'Evet, servisi kullanacağım' },
              { value: false, label: 'Hayır, servis istemiyorum' },
            ].map((opt) => {
              const selected = servisIstiyor === opt.value
              return (
                <label
                  key={String(opt.value)}
                  htmlFor={`servis-${opt.value}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    padding: '0.6rem 0.9rem',
                    borderRadius: '14px',
                    border: '1px solid ' + (selected ? '#285843' : '#d8d8d0'),
                    backgroundColor: selected ? '#eef5f1' : '#fff',
                    color: '#285843',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  }}
                >
                  <input
                    id={`servis-${opt.value}`}
                    type='radio'
                    name='servisIstiyor'
                    checked={selected}
                    onChange={() => setServisIstiyor(opt.value)}
                    style={{ accentColor: '#285843', width: '1.05rem', height: '1.05rem' }}
                  />
                  <span style={{ fontSize: '0.95rem', fontWeight: selected ? 600 : 400 }}>{opt.label}</span>
                </label>
              )
            })}
          </div>

          {/* Önemli Bilgilendirme */}
          <div
            style={{
              marginLeft: '1rem',
              marginRight: '1rem',
              marginTop: '0.5rem',
              marginBottom: '0.5rem',
              padding: '0.85rem 1rem',
              borderRadius: '12px',
              backgroundColor: '#fff8e1',
              border: '1px solid #f0c674',
              color: '#7a5d00',
              fontSize: '0.82rem',
              lineHeight: 1.55,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.9rem' }}>⚠️ Önemli Bilgilendirme</div>
            <ul style={{ paddingLeft: '1.1rem', marginBottom: 0 }}>
              <li>
                Yeni kayıtlar, her hafta <strong>Çarşamba günü saat 09.00</strong>'da açılacaktır.
              </li>
              <li>Daha önce başvuru yapan adaylar, tekrar başvuru yapamayacaktır.</li>
              <li>
                Başvurusu kabul edilen öğrenciler, bilgilendirme amacıyla oluşturulacak <strong>WhatsApp grubuna</strong> eklenecektir.
              </li>
              <li>
                Servis hizmeti ve diğer tüm duyuru ve bilgilendirmeler <strong>WhatsApp grubu</strong> üzerinden yapılacaktır.
              </li>
              <li>
                Başvuru işlemini tamamlayan veliler, çocuğunun etkinliğe katılımına engel teşkil edecek herhangi bir <strong>sağlık sorunu bulunmadığını beyan ve kabul</strong> etmiş sayılacaktır.
              </li>
            </ul>
          </div>

          <ConditionsCheckBox
            checkbox={checkbox}
            setCheckbox={setCheckbox}
            basvuru_sartlari_aciklama={state?.basvuru_sartlari_aciklama}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              marginTop: '1rem',
              marginLeft: '1rem',
              marginRight: '1rem',
              padding: '0.75rem',
              borderRadius: '12px',
              backgroundColor: '#f7faf8',
              border: '1px solid #d8e5dc',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '1rem',
              marginBottom: '2rem',
            }}
          >
            <Button
              block
              onClick={onFinish}
              disabled={!!preview?.onceki_basvuru || preview?.kontenjan?.doldu}
              style={{
                marginLeft: '1rem',
                marginRight: '1rem',
                backgroundColor: '#285843',
                border: 'none',
                borderRadius: '14px',
                padding: '0.75rem 1rem',
                fontWeight: 600,
                letterSpacing: '0.2px',
                boxShadow: '0 4px 14px rgba(40, 88, 67, 0.25)',
              }}
            >
              {loading ? <Spinner size={'sm'} /> : 'Başvuruyu Tamamla'}
            </Button>
          </div>
        </div>
      )}

      <WarningModal isModal={isModal.active} messageType={isModal.type} messageTitle={isModal?.title} errorMessage={isModal?.message} toggleWarningModal={toggleWarningModal} />

      <Modal isOpen={confirmModal.open} centered toggle={() => !loading && setConfirmModal({ open: false, data: null })}>
        <ModalBody style={{ padding: '1.4rem 1.4rem 0.6rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '0.9rem' }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#285843' }}>Başvuru Özeti</div>
            <div style={{ fontSize: '0.78rem', color: '#7a8a82', marginTop: '0.2rem' }}>Aşağıdaki bilgilerle başvurunuz kaydedilecektir</div>
          </div>

          {confirmModal.data && (
            <div
              style={{
                backgroundColor: '#f7faf8',
                border: '1px solid #d8e5dc',
                borderRadius: '12px',
                padding: '0.9rem 1rem',
                fontSize: '0.88rem',
                color: '#285843',
                lineHeight: 1.7,
              }}
            >
              <Row label='Hafta' value={`${confirmModal.data.haftaNo}. Hafta`} bold />
              <Row label='Başvuru Türü' value={confirmModal.data.applicantTypeLabel} />
              <Row label='Katılımcı' value={confirmModal.data.katilimciAdSoyad} bold />
              <Row label='Yaş Grubu' value={confirmModal.data.yasGrubuLabel} bold />
              <Row label='Ders Günleri' value={confirmModal.data.dersGunleri} bold />
              <Row label='Ders Saati' value='10.00 - 15.00' bold />
              <hr style={{ margin: '0.6rem 0', borderColor: '#d8e5dc' }} />
              <Row label='Ad Soyad' value={confirmModal.data.veliAdSoyad} />
              <Row label='Telefon' value={confirmModal.data.veliTelefon} />
              <hr style={{ margin: '0.6rem 0', borderColor: '#d8e5dc' }} />
              <Row label='İlçe' value={confirmModal.data.ilceLabel} />
              <Row label='Mahalle' value={confirmModal.data.mahalleLabel} />
              <Row label='Okul' value={confirmModal.data.okulLabel} />
              <Row label='Adres' value={confirmModal.data.adres} />
              <hr style={{ margin: '0.6rem 0', borderColor: '#d8e5dc' }} />
              <Row label='Servis' value={confirmModal.data.servisLabel} bold />
            </div>
          )}

          <div
            style={{
              marginTop: '0.9rem',
              fontSize: '0.78rem',
              color: '#7a5d00',
              backgroundColor: '#fff8e1',
              border: '1px solid #f0c674',
              borderRadius: '10px',
              padding: '0.55rem 0.75rem',
              textAlign: 'center',
            }}
          >
            Bilgiler doğru mu? Başvurunuz bu şekilde kaydedilecek.
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: 'none', padding: '0.6rem 1.2rem 1.2rem' }}>
          <Button
            onClick={() => setConfirmModal({ open: false, data: null })}
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: '#fff',
              color: '#285843',
              border: '1px solid #d8e5dc',
              borderRadius: '12px',
              padding: '0.65rem',
              fontWeight: 600,
            }}
          >
            Vazgeç
          </Button>
          <Button
            onClick={submitToBackend}
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: '#285843',
              border: 'none',
              borderRadius: '12px',
              padding: '0.65rem',
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(40, 88, 67, 0.25)',
            }}
          >
            {loading ? <Spinner size='sm' /> : 'Onayla'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default TabiatBasvuruForm
